import type {
  ModelsFanReferralLinkResponse,
  ModelsFanReferralStatsResponse
} from '@/api/generated-api'
import { createQueryHook } from '@/shared/api'
import api from '@/shared/api/client'

export const enum ReferralsQueryKeys {
  REFERRAL_LINK = 'referral_link',
  REFERRAL_STATS = 'referral_stats',
  REFERRAL_INVITED = 'referral_invited'
}

export interface ReferralUser {
  id: string
  nickname: string
  dateJoined: Date
  dateActive: Date | null
  earnedPoints: number
}

interface FanReferralEntryResponse {
  referral_id?: string
  referred_user_id?: string
  nickname?: string
  date_joined?: string
  date_active?: string | null
  earned_points?: number
}

interface FanReferralsInvitedResponse {
  referrals?: FanReferralEntryResponse[]
  total_count?: number
}

export interface ReferralsInvitedData {
  referrals: ReferralUser[]
  totalCount: number
}

const parseDate = (value?: string | null) => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const mapReferralEntry = (
  entry: FanReferralEntryResponse
): ReferralUser | null => {
  const dateJoined = parseDate(entry.date_joined)
  if (!dateJoined) return null

  return {
    id:
      entry.referral_id ||
      entry.referred_user_id ||
      entry.nickname ||
      entry.date_joined ||
      '',
    nickname: entry.nickname || entry.referred_user_id || '—',
    dateJoined,
    dateActive: parseDate(entry.date_active),
    earnedPoints: entry.earned_points ?? 0
  }
}

const getReferralLink = async (): Promise<ModelsFanReferralLinkResponse> =>
  api.v2ReferralsMeLinkList()

const getReferralsStats = async (): Promise<ModelsFanReferralStatsResponse> =>
  api.v2ReferralsMeStatsList()

const getReferralsInvited = async ({
  limit,
  offset
}: {
  limit: number
  offset: number
}): Promise<ReferralsInvitedData> => {
  const response = (await api.v2ReferralsMeInvitedList({
    limit,
    offset
  })) as unknown as FanReferralsInvitedResponse

  return {
    referrals: (response.referrals ?? []).flatMap(entry => {
      const referral = mapReferralEntry(entry)
      return referral ? [referral] : []
    }),
    totalCount: response.total_count ?? 0
  }
}

const useReferralLinkFactory = createQueryHook<
  { enabled?: boolean },
  ModelsFanReferralLinkResponse
>({
  getQueryKey: () => [ReferralsQueryKeys.REFERRAL_LINK],
  queryFn: getReferralLink,
  getEnabled: params => params.enabled ?? true,
  getOptions: () => ({
    staleTime: 60_000,
    refetchOnMount: true
  })
})

const useReferralsStatsFactory = createQueryHook<
  Record<string, never>,
  ModelsFanReferralStatsResponse
>({
  getQueryKey: () => [ReferralsQueryKeys.REFERRAL_STATS],
  queryFn: getReferralsStats,
  getOptions: () => ({
    staleTime: 60_000,
    refetchOnMount: true
  })
})

const useReferralsInvitedFactory = createQueryHook<
  { limit: number; offset: number },
  ReferralsInvitedData
>({
  getQueryKey: ({ limit, offset }) => [
    ReferralsQueryKeys.REFERRAL_INVITED,
    limit,
    offset
  ],
  queryFn: getReferralsInvited,
  getOptions: () => ({
    staleTime: 60_000,
    refetchOnMount: true
  })
})

export function useReferralLink() {
  return useReferralLinkFactory({})
}

export function useReferralLinkEnabled(enabled: boolean) {
  return useReferralLinkFactory({ enabled })
}

export function useReferralsStats() {
  return useReferralsStatsFactory({})
}

export function useReferralsInvited({
  limit = 100,
  offset = 0
}: {
  limit?: number
  offset?: number
} = {}) {
  return useReferralsInvitedFactory({ limit, offset })
}
